import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const KEY_TOKEN = "jw_admin_token";
const KEY_USER = "jw_admin_user";

const AdminContext = createContext(null);

export function AdminProvider({ children }) {
    const [token, setToken] = useState(() => localStorage.getItem(KEY_TOKEN));
    const [user, setUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(KEY_USER)) || null;
        } catch {
            return null;
        }
    });

    const login = useCallback(async (email, password) => {
        const r = await api.post("/admin/login", { email, password });
        const u = {
            email: r.data.email,
            role: r.data.role,
            name: r.data.name,
            modules: r.data.modules || [],
        };
        localStorage.setItem(KEY_TOKEN, r.data.token);
        localStorage.setItem(KEY_USER, JSON.stringify(u));
        setToken(r.data.token);
        setUser(u);
        return u;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(KEY_TOKEN);
        localStorage.removeItem(KEY_USER);
        setToken(null);
        setUser(null);
    }, []);

    // Validate token on mount
    useEffect(() => {
        if (!token) return;
        api.get("/admin/me", { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                const u = {
                    email: r.data.email,
                    role: r.data.role,
                    name: r.data.name,
                    modules: r.data.modules || [],
                };
                setUser(u);
                localStorage.setItem(KEY_USER, JSON.stringify(u));
            })
            .catch(() => logout());
    }, [token, logout]);

    return (
        <AdminContext.Provider value={{
            token, user, login, logout,
            isOwner: user?.role === "owner",
            modules: user?.modules || [],
            hasModule: (m) => user?.role === "owner" || (user?.modules || []).includes(m),
        }}>
            {children}
        </AdminContext.Provider>
    );
}

export function useAdmin() {
    const ctx = useContext(AdminContext);
    if (!ctx) throw new Error("useAdmin must be used inside AdminProvider");
    return ctx;
}
